import { matchPath } from 'react-router-dom';
import type { Store } from 'redux';

import type { EventAliasingService } from '@provider-portal/common/services/EventAliasingService';
import type { MixpanelReplayContext } from '@provider-portal/common/services/MixpanelReplayController';
import type { MixpanelService } from '@provider-portal/common/services/MixpanelService';

import { selectSelectedProvider } from 'rdx/account/selectors';
import type { GlobalAppState } from 'rdx/state';
import {
  selectMixpanelReplayDecision,
  selectTargetingSessionContext,
} from 'rdx/targetingParameters/selectors';
import type { ProviderPortalModuleManifest } from 'root/ProviderPortalApp';

import { AppRoutes } from '../../routing/AppRoutes';

interface ReplayServices {
  mixpanelService: Pick<MixpanelService, 'updateReplayContext' | 'suspendReplay'>;
  eventAliasingService: Pick<EventAliasingService, 'hasLeadIdStored' | 'linkLeadAndProvider'>;
}

export function connectMixpanelReplay(
  store: Pick<Store<GlobalAppState>, 'getState' | 'subscribe'>,
  { mixpanelService, eventAliasingService }: ReplayServices,
  moduleManifests: readonly Pick<ProviderPortalModuleManifest, 'moduleName' | 'isPublic'>[],
): () => void {
  const excludedRoutes = [
    AppRoutes.AUTH,
    AppRoutes.AUTH_WITH_TOKEN,
    AppRoutes.LOGOUT,
    AppRoutes.FORGOT_PASSWORD,
    AppRoutes.UNLOCK_ACCOUNT,
    ...moduleManifests
      .filter((manifest) => manifest.isPublic)
      .map((manifest) => `/${manifest.moduleName}(/*)`),
  ];
  let previous: MixpanelReplayContext | null = null;
  let failedContext: MixpanelReplayContext | null = null;
  let latest: { context: MixpanelReplayContext } | null = null;
  let active = true;
  let waitingForSession = false;

  const sameContext = (left: MixpanelReplayContext | null, right: MixpanelReplayContext) =>
    left !== null &&
    left.identity?.distinctId === right.identity?.distinctId &&
    left.identity?.sessionId === right.identity?.sessionId &&
    left.decision === right.decision &&
    left.providerId === right.providerId &&
    left.captureAllowed === right.captureAllowed;

  const publish = (request: { context: MixpanelReplayContext }) => {
    const markFailed = () => {
      if (!active || latest !== request) return false;
      previous = null;
      failedContext = request.context;
      return true;
    };
    void mixpanelService.updateReplayContext(request.context).catch(() => {
      if (!markFailed()) return;
      void Promise.resolve().then(() => {
        if (!active || latest !== request) return;
        previous = request.context;
        void mixpanelService.updateReplayContext(request.context).catch(markFailed);
      });
    });
  };

  const synchronize = () => {
    const state = store.getState();
    if (!state.session._persist.rehydrated) {
      if (!waitingForSession) {
        waitingForSession = true;
        latest = null;
        previous = null;
        failedContext = null;
        void mixpanelService.suspendReplay();
      }
      return;
    }
    waitingForSession = false;
    const session = selectTargetingSessionContext(state);
    const pathname = state.router.location?.pathname;
    const captureAllowed =
      session !== null &&
      pathname !== undefined &&
      !excludedRoutes.some((path) => matchPath(pathname, { path }));
    const next: MixpanelReplayContext = {
      identity:
        session === null
          ? null
          : {
              distinctId: `provider-portal-account-${session.accountId}`,
              sessionId: session.sessionId,
            },
      decision: captureAllowed ? selectMixpanelReplayDecision(state) : null,
      providerId: session === null ? null : selectSelectedProvider(state)?.providerId ?? null,
      captureAllowed,
    };

    if (sameContext(previous, next) || sameContext(failedContext, next)) return;

    const accountChanged = latest?.context.identity?.distinctId !== next.identity?.distinctId;
    const request = { context: next };
    latest = request;
    previous = next;
    failedContext = null;
    publish(request);
    if (accountChanged && next.identity !== null && eventAliasingService.hasLeadIdStored()) {
      eventAliasingService.linkLeadAndProvider(next.identity.distinctId);
    }
  };

  const unsubscribe = store.subscribe(synchronize);
  synchronize();

  return () => {
    active = false;
    latest = null;
    unsubscribe();
    void mixpanelService.suspendReplay();
  };
}
